// /connect/github — where GitHub sends the person back while connecting
// (components/github-connect.tsx):
//   ?code&state            the App was created → store it, go install it
//   ?installation_id&…     the App was installed → remember where, done

import { useEffect, useRef, useState } from 'react'
import { Link, useNavigate, useSearch } from '@tanstack/react-router'
import { ApiError, githubApi } from '../lib/api-client'
import { useWorkspaceId } from '../lib/auth'
import { useDialogs } from '../components/dialogs'
import { Notice, Panel } from '../components/ui'
import { MomentArt } from '../components/goblin-art'

export interface ConnectGitHubSearch {
  code?: string
  state?: string
  installation_id?: string
  setup_action?: string
}

/** Where to land after connecting; set by whoever started the round trip. */
export const GITHUB_RETURN_KEY = 'goblic.github.returnTo'

function takeReturnTo(): string | null {
  try {
    const value = sessionStorage.getItem(GITHUB_RETURN_KEY)
    sessionStorage.removeItem(GITHUB_RETURN_KEY)
    return value && value.startsWith('/') ? value : null
  } catch {
    return null
  }
}

export function ConnectGitHubPage() {
  const search = useSearch({ strict: false }) as ConnectGitHubSearch
  const workspaceId = useWorkspaceId()
  const navigate = useNavigate()
  const { alert } = useDialogs()
  const [error, setError] = useState<string | null>(null)
  const started = useRef(false)

  useEffect(() => {
    if (!workspaceId || started.current) return
    started.current = true
    void (async () => {
      try {
        if (search.code && search.state) {
          const { installUrl } = await githubApi.completeManifest({
            workspaceId,
            code: search.code,
            state: search.state,
          })
          window.location.assign(installUrl)
          return
        }
        if (search.installation_id) {
          const installation = await githubApi.recordInstallation({
            workspaceId,
            installationId: Number(search.installation_id),
          })
          // Started from the welcome (or elsewhere)? Go back there.
          const returnTo = takeReturnTo()
          await navigate({ to: returnTo ?? '/credentials' })
          await alert({
            kind: 'success',
            title: 'GitHub is connected',
            message: `Runs can now push and open draft pull requests in ${
              installation.repositorySelection === 'all'
                ? 'all repositories'
                : 'the chosen repositories'
            } of ${installation.account}.`,
            art: '/goblins/dialog-connected.webp',
          })
          return
        }
        setError(
          'GitHub didn’t send anything to finish. Start connecting again from Credentials.'
        )
      } catch (err) {
        setError(
          err instanceof ApiError
            ? err.message
            : 'Connecting GitHub failed. Start again from Credentials.'
        )
      }
    })()
  }, [workspaceId, search, navigate, alert])

  return (
    <div className="mx-auto mt-16 max-w-md">
      <Panel>
        <div className="flex flex-col items-center gap-3 text-center">
          <MomentArt moment={error ? 'failed' : 'waiting'} size={96} />
          {error ? (
            <>
              <Notice tone="error">{error}</Notice>
              <Link to="/credentials" className="btn-secondary">
                Back to Credentials
              </Link>
            </>
          ) : (
            <p className="text-base text-ink-2">
              Finishing the connection with GitHub…
            </p>
          )}
        </div>
      </Panel>
    </div>
  )
}
